import React, { useEffect, useRef, useState } from 'react';
import { LuEye, LuEyeClosed } from 'react-icons/lu';
import { NumericFormat } from "react-number-format";
import { Button } from './Button';

export const InputNumber = ({
    value,
    onChange,
    placeholder = '',
    label = '',
    required = false,
    maxLength = 9999999,
    marginTop = false,
    name = '',
    className = ""
}) => {
    const handleChange = (e) => {
        const onlyNumbers = e.target.value.replace(/\D/g, ''); // remove tudo que não for número
        onChange({ target: { name, value: onlyNumbers } });
    };

    return (
        <div className={`flex flex-col space-y-1 ${marginTop ? 'mt-4' : ''}`}>
            {label && (
                <label htmlFor={name} className="text-sm text-gray-500">
                    {label} {required && <small className="text-red-500">*(obrigatório)</small>}
                </label>
            )}

            <input
                name={name}
                required={required}
                onChange={handleChange}
                maxLength={maxLength}
                type="text"
                value={value}
                placeholder={placeholder}
                className={`w-full px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`}
            />
        </div>
    );
};


export const Input = ({
    value,
    onChange,
    placeholder = '',
    type = 'text',
    label = '',
    required = false,
    maxLength = 9999999,
    marginTop = false,
    name = '',
    className = ""
}) => {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';

    const togglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };

    return (
        <div className={`flex flex-col space-y-1 ${marginTop && 'mt-4'}`}>
            {label && (
                <label htmlFor={name} className="text-sm text-gray-500">
                    {label} {required && <small className="text-red-500">*(obrigatório)</small>}
                </label>
            )}

            <div className="relative">
                <input
                    name={name}
                    required={required}
                    onChange={onChange}
                    maxLength={maxLength}
                    type={isPassword && showPassword ? 'text' : type}
                    value={value}
                    placeholder={placeholder}
                    className={`w-full px-4 py-2 pr-10 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`}
                />

                {isPassword && (
                    <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 focus:outline-none"
                        tabIndex={-1}
                    >
                        {showPassword ? <LuEyeClosed size={18} /> : <LuEye size={18} />}
                    </button>
                )}
            </div>
        </div>
    );
};

export const CodeInput = ({ onComplete }) => {
    const inputsRef = useRef([]);

    const handleChange = (index, e) => {
        let value = e.target.value.toUpperCase();

        if (/^[A-Z0-9]$/.test(value)) {
            e.target.value = value;
            if (index < 4) {
                inputsRef.current[index + 1]?.focus();
            }
        } else {
            e.target.value = '';
        }

        const code = inputsRef.current.map(input => input?.value).join('');
        if (code.length === 4 && !code.includes(' ')) {
            onComplete?.(code);
        }
    };


    const handleKeyDown = (index, e) => {
        if (e.key === "Backspace" && !e.target.value && index > 0) {
            inputsRef.current[index - 1]?.focus();
        }
    };

    return (
        <div className="flex gap-2 justify-center">
            {[...Array(4)].map((_, i) => (
                <input
                    key={i}
                    type="text"
                    maxLength={1}
                    ref={(el) => (inputsRef.current[i] = el)}
                    onChange={(e) => handleChange(i, e)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    className="w-12 h-12 text-center border border-gray-300 rounded-lg text-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
            ))}
        </div>
    );
};

export const InputPrice = ({ prefix, onChangeValue, value, label, disabled = false, className = '' }) => (
    <div className={"flex flex-col space-y-1" + className}>
        {label && <label htmlFor="category" className="text-sm text-gray-500">
            {label}
        </label>}
        <NumericFormat
            disabled={disabled}
            value={value}
            thousandSeparator="."
            decimalSeparator=","
            prefix={`${prefix} `}
            decimalScale={2}
            onValueChange={({ floatValue }) => onChangeValue?.(floatValue || 0)}
            placeholder="Digite o valor"
            className={`${disabled && 'bg-gray-300'} w-full px-4 py-2 pr-10 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500`}
        />
    </div>
);

export const TokenInput = ({ length = 4, onComplete, label, onResend, hideBtnForwarding = false, children, labelChildren = '' }) => {
    const inputsRef = useRef([]);
    const [secondsLeft, setSecondsLeft] = useState(180);

    const handleInput = (e, index) => {
        const value = e.target.value.replace(/[^a-zA-Z0-9]/g, ""); // aceita letras e números
        if (!value) return;

        const chars = value.split("");

        // preenche os campos a partir do índice atual
        for (let i = 0; i < chars.length && index + i < length; i++) {
            const input = inputsRef.current[index + i];
            if (input) {
                input.value = chars[i].toUpperCase(); // opcional: transforma em maiúsculo
            }
        }

        const nextIndex = index + chars.length;
        if (nextIndex < length) {
            inputsRef.current[nextIndex]?.focus();
        } else {
            inputsRef.current[length - 1]?.blur(); // remove foco se terminou
        }

        checkComplete();
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace") {
            const input = inputsRef.current[index];
            if (input && input.value === "") {
                inputsRef.current[index - 1]?.focus();
            }
        }
    };

    const checkComplete = () => {
        const code = inputsRef.current.map(input => input?.value || "").join("");
        if (code.length === length) {
            onComplete(code);
        }
    };

    const handleResendClick = () => {
        if (secondsLeft === 0) {
            onResend && onResend();
            setSecondsLeft(180); // 3 minutos
        }
    };

    useEffect(() => {
        if (secondsLeft <= 0) return;

        const timer = setInterval(() => {
            setSecondsLeft(prev => prev - 1);
        }, 1000);

        return () => clearInterval(timer);
    }, [secondsLeft]);

    const formatTime = (sec) => {
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${m}:${s.toString().padStart(2, "0")}`;
    };

    useEffect(() => { handleResendClick() }, []);

    const onPaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text").replace(/[^a-zA-Z0-9]/g, "");
        const chars = pastedData.slice(0, length).split("");

        // Preenche os campos
        inputsRef.current.forEach((input, i) => {
            if (input) input.value = chars[i] || "";
        });

        if (chars.length === length) {
            inputsRef.current[length - 1]?.blur();
            checkComplete();
        } else {
            inputsRef.current[chars.length]?.focus();
        }
    };

    return (
        <div className="flex flex-col items-center space-y-1">
            {label && (
                <label className="text-sm text-gray-500">
                    {label}
                </label>
            )}
            <div className="flex space-x-2">
                {Array.from({ length }).map((_, i) => (
                    <input
                        onPaste={onPaste}
                        key={i}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        ref={(el) => {
                            inputsRef.current[i] = el;
                        }}
                        onChange={(e) => handleInput(e, i)}
                        onKeyDown={(e) => handleKeyDown(e, i)}
                        className="w-12 h-12 text-center text-xl border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                ))}
            </div>

            {secondsLeft > 0 ? (
                <small className="text-gray-400">
                    {`Aguarde ${formatTime(secondsLeft)} para reenviar`}
                </small>
            ) : (
                <>
                    <small className="text-gray-400">
                        Não recebeu?{" "}
                        <button
                            onClick={handleResendClick}
                            className="cursor-pointer duration-500 hover:underline hover:text-gray-700"
                        >
                            clique aqui
                        </button>
                    </small>
                    {children && children}
                </>
            )}
        </div >
    );
};
